import {
    ApiErrorResponse,
    ApiSuccessResponse,
} from '@calcom/atoms/dist/packages/platform/types/api';
import { context } from '@shared-services/context-service';
import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { useIsHydrated } from 'client/widget-components/customHooks/useIsHydrated';
import { buyBooking } from 'client/widget-components/ecom/services/snipcartService';
import React, { useCallback, useMemo, useState } from 'react';
import calApi from './api/calApi';
import BookerSkeleton from './components/BookerSkeleton';
import BookingMarkup from './components/BookingMarkup';
import BookingPricingMetaSection from './components/BookingPricingMetaSection';
import { BookingCancellation } from './components/ConfirmationModal/BookingCancellation';
import BookingConfirmation from './components/ConfirmationModal/BookingConfirmation';
import BookingConfirmationDryRun from './components/ConfirmationModal/BookingConfirmationDryRun';
import BookingConfirmationPending from './components/ConfirmationModal/BookingConfirmationPending';
import RescheduleModal from './components/ConfirmationModal/RescheduleModal';
import { BookerContextProvider } from './Context';
import { useBookerProps } from './hooks/useBookerProps';
import useCalEventData from './hooks/useCalEventData';
import { useRuntimeCalBookingState } from './hooks/useRuntimeCalBookingState';
import { Booker, LazyBookerWrapper } from './LazyBooker';
import { logBookingError, logBookingInfo } from './loggingUtils';
import { CalBookingWrapper, containerQuery } from './styles';
import {
    BookerContextType,
    BookerStoreValues,
    Booking,
    BookingStatus,
    CreateBookingInput,
    DesignStyle,
    FeatureMetadata,
    PriceInfo,
    PricingType,
} from './types';
import {
    bookingObjToEcomConverter,
    createDryRunBookingPayload,
    getBookerClassNames,
    getBookerMetadata,
    resolveCalProviderLanguage,
    shouldUsePaidBooking,
    validateBookerReceivedParams,
} from './utils';

export interface CalBookingProps {
    clientId: string;
    userName: string;
    eventSlug: string;
    eventIdentifier: string;
    localeCode?: any;
    teamId: number;
    featureMetadata: FeatureMetadata;
    widgetId: string;
    designStyle: DesignStyle | null;
    calApiDomain: string;
    calEventId: number;
    organizationId: number;
    priceInfo: PriceInfo;
    // @deprecated
    price: number | null;
    // @deprecated
    pricingType: PricingType;
}

const RuntimeCalBooking: RuntimeWidget = (props: CalBookingProps) => {
    const [state, actions] = useRuntimeCalBookingState();

    const {
        isLoading,
        confirmButtonDisabled,
        bookerKey,
        rescheduleUid,
        cancelBookingId,
        confirmationData,
        calEventData,
    } = state;

    const {
        setRescheduleUid,
        setCancelBookingId,
        clearConfirmationData,
        onBookingSuccess,
        setConfirmButtonDisabled,
        setIsLoading,
        resetBookerState,
        setCalEventData,
    } = actions;

    const [{ editor, language, apiUrl }] = useState(() => {
        calApi.setCalDomain(props.calApiDomain);
        calApi.setCalClientId(props.clientId);

        return {
            editor: useEditor(),
            language: resolveCalProviderLanguage(),
            apiUrl: calApi.apiUrl,
        };
    });

    const hydrated = useIsHydrated();

    const {
        clientId,
        featureMetadata,
        widgetId,
        organizationId,
        priceInfo,
        eventIdentifier,
        designStyle: designStyleProp,
    } = props;

    const isBookingDryRun =
        editor.isInPreviewOrEditor() ||
        context.isServer ||
        !featureMetadata.isNativeBookingPublishingAvailable;

    const { eventSlug, teamId, userName } = useBookerProps({
        ...props,
        isBookingDryRun,
        setRescheduleUid,
        setCancelBookingId,
    });

    useCalEventData(
        {
            teamId,
            eventSlug,
            organizationId: props.organizationId,
        },
        {
            onSuccess: setCalEventData,
        }
    );

    const isBookingActive = !!eventSlug;
    const designStyle = designStyleProp || DesignStyle.SITE_THEME;

    const bookerClassNames = useMemo(
        () => getBookerClassNames(designStyle),
        [designStyle]
    );

    const bookerContext: BookerContextType = {
        ...priceInfo,
        ...featureMetadata,
        eventSlug,
        teamId,
        userName,
        rescheduleUid,
        cancelBookingId,
        widgetId,
        designStyle,
        language,
        apiUrl,
        clientId,
        organizationId,
        pricingType: priceInfo?.pricingType || PricingType.FREE,
        eventIdentifier,
        isBookingDryRun,
    };

    // validate all required props are present for booker atom when booking is active
    validateBookerReceivedParams(bookerContext, isBookingActive);

    if (!isBookingActive && !editor.isInPreviewOrEditor()) {
        return null;
    }

    const rescheduleBooking = () => {
        if (!confirmationData?.uid) {
            return;
        }
        setRescheduleUid(confirmationData.uid);
        clearConfirmationData();
    };

    const handleBookingSuccess = (res: ApiSuccessResponse<any>) => {
        // Extract booking data from the API response structure
        const bookingData = res.data as Booking;
        onBookingSuccess(bookingData);
    };

    const handleCreatePaidBooking = async (
        createBookingInput: CreateBookingInput
    ) => {
        try {
            setConfirmButtonDisabled(true);

            // We should allow to proceed to checkout for disabled Paid feature (mocked booking feature)
            if (!editor.insideEditor() && calEventData) {
                await buyBooking(
                    bookingObjToEcomConverter(createBookingInput, calEventData)
                );
                resetBookerState();
            } else {
                onBookingSuccess(createDryRunBookingPayload());
                logBookingInfo(
                    'Tried to create paid booking without calEventData',
                    {
                        additionalInfoToLog: bookerContext,
                    }
                );
            }
        } catch (error) {
            logBookingError('Failed to create paid booking', {
                errorMessage: (error as Error).message,
                additionalInfoToLog: bookerContext,
            });
        } finally {
            setConfirmButtonDisabled(false);
        }
    };

    const onInitBooker = () => {
        setIsLoading(false);
    };

    const onBookerStateChange = useCallback(
        (bookerState: BookerStoreValues) => {
            if (bookerState.state === 'selecting_time' && isLoading) {
                onInitBooker();
            }
        },
        [isLoading]
    );

    const showDryRunConfirmation = confirmationData && isBookingDryRun;
    const showConfirmation =
        confirmationData &&
        !isBookingDryRun &&
        confirmationData?.status === BookingStatus.ACCEPTED;
    const showPendingConfirmation =
        confirmationData &&
        !isBookingDryRun &&
        confirmationData?.status === BookingStatus.PENDING;
    // We want to render the booker only after the component is hydrated on
    // client side since it's a lazy loaded component and we want to keep the
    // server and client rendering consistent.
    const shouldRenderBooker = hydrated && isBookingActive;
    const shouldRenderLoadingSkeleton = isBookingActive && isLoading;
    const shouldRenderBookingMarkup = !isBookingActive;
    const showRescheduleModal = !isBookingDryRun && rescheduleUid;

    const calBookerWrapperClassNames: string[] = [designStyle];
    if (isBookingActive) {
        calBookerWrapperClassNames.push(
            isLoading ? 'booker-loading' : 'booker-ready'
        );
    }

    const commonBookerProps = {
        eventSlug,
        customClassNames: bookerClassNames,
        // If the booking is a paid type and the widget is not in reschedule mode, we want to use the paid booking flow
        // For reschedule mode - use the free booking flow since the booking is already paid
        ...(shouldUsePaidBooking(bookerContext) && !rescheduleUid
            ? {
                  handleCreateBooking: handleCreatePaidBooking,
                  handleSlotReservation: async () => {
                      // no-op
                      // need to be passed to the booker atom to avoid internal reservation operation
                  },
              }
            : { onCreateBookingSuccess: handleBookingSuccess }),
        onCreateBookingError: (resOrError: ApiErrorResponse | Error) => {
            logBookingError('Failed to create booking inside Booker atom', {
                ...(resOrError instanceof Error
                    ? {
                          errorMessage: resOrError.message,
                          additionalInfoToLog: bookerContext,
                      }
                    : {
                          errorMessage: resOrError.error.message,
                          errorCode: resOrError.error.code,
                          errorDetails: resOrError.error.details,
                          additionalInfoToLog: bookerContext,
                      }),
            });
        },
        onReserveSlotError: (res: ApiErrorResponse) => {
            logBookingError('Failed to reserve slot inside Booker atom', {
                errorMessage: res.error.message,
                errorCode: res.error.code,
                errorDetails: res.error.details,
                additionalInfoToLog: bookerContext,
            });
        },
        onDeleteSlotError: (res: ApiErrorResponse) => {
            logBookingError('Failed to delete slot inside Booker atom', {
                errorMessage: res.error.message,
                errorCode: res.error.code,
                errorDetails: res.error.details,
                additionalInfoToLog: bookerContext,
            });
        },
        showNoAvailabilityDialog: false,
        roundRobinHideOrgAndTeam: true,
        eventMetaChildren: <BookingPricingMetaSection />,
        metadata: getBookerMetadata(bookerContext, calEventData),
        confirmButtonDisabled,
    };

    return (
        <CalBookingWrapper
            data-auto='ssr-cal-booking-widget'
            className={calBookerWrapperClassNames.join(' ')}
        >
            <BookerContextProvider {...bookerContext}>
                <style
                    dangerouslySetInnerHTML={{
                        __html: containerQuery(widgetId),
                    }}
                />
                <div className='booker-wrapper'>
                    {shouldRenderBooker && (
                        <LazyBookerWrapper
                            clientId={clientId}
                            apiUrl={apiUrl}
                            language={language}
                        >
                            <Booker
                                key={bookerKey}
                                {...(teamId
                                    ? { isTeamEvent: true, teamId }
                                    : { username: userName })}
                                isBookingDryRun={isBookingDryRun}
                                onBookerStateChange={onBookerStateChange}
                                {...commonBookerProps}
                            />
                            {showRescheduleModal && (
                                <RescheduleModal
                                    bookerProps={{
                                        bookingUid: rescheduleUid,
                                        isTeamEvent: true,
                                        teamId,
                                        ...commonBookerProps,
                                    }}
                                    onClose={() => setRescheduleUid(null)}
                                />
                            )}
                        </LazyBookerWrapper>
                    )}
                    {showConfirmation && (
                        <BookingConfirmation
                            confirmationData={confirmationData}
                            closeConfirmation={clearConfirmationData}
                            rescheduleBooking={rescheduleBooking}
                            setCancelBookingId={setCancelBookingId}
                        />
                    )}
                    {showPendingConfirmation && (
                        <BookingConfirmationPending
                            confirmationData={confirmationData}
                            closeConfirmation={clearConfirmationData}
                            rescheduleBooking={rescheduleBooking}
                            setCancelBookingId={setCancelBookingId}
                        />
                    )}
                    {showDryRunConfirmation && (
                        <BookingConfirmationDryRun
                            closeConfirmation={clearConfirmationData}
                        />
                    )}
                    {cancelBookingId && (
                        <BookingCancellation
                            bookingUid={cancelBookingId}
                            closeCancellation={() => setCancelBookingId(null)}
                        />
                    )}
                    {shouldRenderLoadingSkeleton && <BookerSkeleton />}
                    {shouldRenderBookingMarkup && <BookingMarkup />}
                </div>
            </BookerContextProvider>
        </CalBookingWrapper>
    );
};

export default withErrorBoundary({
    Comp: RuntimeCalBooking,
    logProps: true,
    componentName: 'CalBooking',
    additionalInfoToLog: {
        tag: 'booking',
    },
});
